/**
 * useConfirmDialog - Promise-based confirmation dialog hook
 * 
 * Provides a clean API for showing confirmation dialogs that can be awaited.
 * Integrates with ConfirmModal component for consistent FinList styling.
 * 
 * Usage:
 * ```jsx
 * function MyComponent() {
 *   const { confirm, ConfirmDialog } = useConfirmDialog();
 * 
 *   const handleDelete = async () => {
 *     const confirmed = await confirm({
 *       title: 'Delete item?',
 *       message: 'This action cannot be undone.',
 *       variant: 'danger',
 *       confirmText: 'Delete'
 *     });
 *     if (confirmed) {
 *       // proceed with deletion
 *     }
 *   };
 * 
 *   return (
 *     <>
 *       <button onClick={handleDelete}>Delete</button>
 *       <ConfirmDialog />
 *     </>
 *   );
 * }
 * ```
 * 
 * Options:
 * - title: string - Dialog title
 * - message: string - Dialog message
 * - itemName?: string - Item name (used in default message)
 * - confirmText?: string - Confirm button text (default: "Confirm")
 * - cancelText?: string - Cancel button text (default: "Cancel")
 * - variant?: 'danger' | 'warning' | 'info' | 'neutral' | 'archive'
 * - icon?: ReactNode - Custom icon
 */

import { useState, useCallback, useRef } from 'react';
import ConfirmModal from '@/components/ui/ConfirmModal';

/**
 * @typedef {Object} ConfirmOptions
 * @property {string} [title] - Dialog title
 * @property {string} [message] - Dialog message
 * @property {string} [itemName] - Item name for default message
 * @property {string} [confirmText] - Confirm button text
 * @property {string} [cancelText] - Cancel button text
 * @property {'danger'|'warning'|'info'|'neutral'|'archive'} [variant] - Color variant
 * @property {React.ReactNode} [icon] - Custom icon
 */

/**
 * Promise-based confirmation dialog hook
 * @returns {{ confirm: (options: ConfirmOptions) => Promise<boolean>, ConfirmDialog: React.FC }}
 */
export function useConfirmDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Store resolve function in a ref to call it later
  const resolveRef = useRef(null);

  /**
   * Show confirmation dialog and return a promise
   * @param {ConfirmOptions} opts - Dialog options
   * @returns {Promise<boolean>} - Resolves to true if confirmed, false if cancelled
   */
  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setOptions(opts);
      setIsOpen(true);
    });
  }, []);

  /**
   * Handle confirm button click
   */
  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    resolveRef.current?.(true);
    resolveRef.current = null;
    setOptions({});
  }, []);

  /**
   * Handle cancel/close
   */
  const handleClose = useCallback(() => {
    setIsOpen(false);
    resolveRef.current?.(false);
    resolveRef.current = null;
    setOptions({});
  }, []);

  /**
   * Confirm with loading state (for async operations)
   * @param {ConfirmOptions} opts - Dialog options
   * @param {() => Promise<void>} asyncAction - Async action to execute on confirm
   * @returns {Promise<boolean>} - Resolves to true if action completed, false if cancelled
   */
  const confirmWithAction = useCallback(async (opts = {}, asyncAction) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setOptions({
        ...opts,
        onConfirmAction: async () => {
          setLoading(true);
          try {
            await asyncAction();
            setIsOpen(false);
            resolve(true);
          } catch (error) {
            // Keep modal open on error, let caller handle it
            resolve(false);
            throw error;
          } finally {
            setLoading(false);
            resolveRef.current = null;
            setOptions({});
          }
        }
      });
      setIsOpen(true);
    });
  }, []);

  /**
   * The dialog component to render
   */
  const ConfirmDialog = useCallback(() => {
    const handleConfirmClick = options.onConfirmAction || handleConfirm;
    
    return (
      <ConfirmModal
        isOpen={isOpen}
        onClose={handleClose}
        onConfirm={handleConfirmClick}
        title={options.title}
        message={options.message}
        itemName={options.itemName}
        confirmText={options.confirmText}
        cancelText={options.cancelText}
        variant={options.variant}
        loading={loading}
        icon={options.icon}
      />
    );
  }, [isOpen, options, loading, handleConfirm, handleClose]);

  return {
    confirm,
    confirmWithAction,
    ConfirmDialog
  };
}

export default useConfirmDialog;
